import React, { useState } from "react";
import {
  Table,
  Card,
  Tag,
  Button,
  Input,
  Popconfirm,
  message,
  Space,
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchCredits_api,
  UpdateCreditStatus_api,
} from "../../Api";

export const ManageCredit = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["credits", page, pageSize, searchText],
    queryFn: () => FetchCredits_api(page, pageSize, searchText),
  });

  const handleMarkAsPaid = async (id: number) => {
    try {
      const res = await UpdateCreditStatus_api(id);
      if (res.isDataUpdated) {
        message.success(res.message || "Credit marked as paid");
        refetch();
      } else {
        message.error(res.message || "Failed to update status");
      }
    } catch (error) {
      message.error("An error occurred");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Inquiry Number",
      dataIndex: "inquiry_number_display", // From backend alias
      key: "inquiry_number_display",
      render: (text: string) => (
        <span className="font-medium">{text || "N/A"}</span>
      ),
    },
    {
      title: "Amount (LKR)",
      dataIndex: "amount",
      key: "amount",
      render: (amount: string) => (
        <span className="font-semibold text-green-600">
          {parseFloat(amount).toLocaleString("en-LK", {
            style: "currency",
            currency: "LKR",
          })}
        </span>
      ),
    },
    {
      title: "Added Date",
      dataIndex: "added_date",
      key: "added_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string | number) => {
        // Assuming 1 is Active/Unpaid and 0 (or 2) is Paid.
        // Based on update logic, we set it to 0 for Paid.
        const isPaid = status == 0 || status == 2;
        return (
          <Tag color={isPaid ? "green" : "red"}>
            {isPaid ? "Paid" : "Unpaid"}
          </Tag>
        );
      },
    },
    {
      title: "Paid Date",
      dataIndex: "paid_date",
      key: "paid_date",
      render: (date: string) => date || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          {record.status != 0 && record.status != 2 && (
            <Popconfirm
              title="Mark as Paid"
              description="Are you sure you want to mark this credit as paid?"
              onConfirm={() => handleMarkAsPaid(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                className="bg-green-600 hover:bg-green-500"
              >
                Paid
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Manage Credit</h1>
        <Input
          placeholder="Search by Inquiry Number..."
          prefix={<SearchOutlined />}
          className="w-full md:w-64"
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <Card className="shadow-sm">
        <Table
          columns={columns}
          dataSource={data?.credits || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
          className="overflow-x-auto"
        />
      </Card>
    </div>
  );
};
